import type { PartnerTemplatePartner } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type { RegistryAsset } from '@/shared/api/transcriptor/registryApi';

import {
  isClinicOwnTemplate,
  isConsultationWithoutBlockNotes,
  isProtocolTemplate,
  parseDocumentType,
  templateDepartmentMessages,
  templateGroupMessages,
  type DocumentTemplateSummary,
  type EncounterSchema,
  type TemplateClassification,
} from '@/entities/templates';

import { CONSULTATION_DOCUMENT_TYPES } from '../config';

import { getSchemaDocumentLabel, getSchemaDocuments } from './schemaDocuments';

export type CatalogOrigin =
  | 'clinic'
  | 'official'
  | 'community'
  | 'design'
  | 'export';

// selectable: linked on demand; required: linked always; outside: an
// organization protocol the treatment schema does not link.
export type CatalogItemState = 'selectable' | 'required' | 'outside';

export type WorkspaceCatalogItem = {
  id: string;
  name: string;
  sectionsCount: number;
  origin: CatalogOrigin;
  state: CatalogItemState;
  description?: string | null;
  category?: string;
  // The heading the item is listed under; items of a group are adjacent.
  group?: string;
  // Set when the template was converted from this partner's template, with
  // the category the partner files it under.
  partner?: PartnerTemplatePartner;
  partnerCategory?: string;
  // Where the template sits in the catalog taxonomy, which a list is
  // filtered by.
  classification?: TemplateClassification;
  // The newer community version offered to the clinic's copy; only the
  // Templates page, where it can be taken, names it.
  communityVersionId?: string;
};

export const getTemplateOrigin = (
  template: Pick<DocumentTemplateSummary, 'source_kind' | 'derived_from_kind'>,
): CatalogOrigin => {
  if (
    template.source_kind === 'system' ||
    template.derived_from_kind === 'system'
  ) {
    return 'official';
  }
  if (
    template.source_kind === 'community' ||
    template.derived_from_kind === 'community'
  ) {
    return 'community';
  }
  return 'clinic';
};

const toCatalogItem = (
  template: DocumentTemplateSummary,
  state: CatalogItemState,
): WorkspaceCatalogItem => ({
  id: template.series_id,
  name: template.name,
  description: template.description,
  sectionsCount: template.sections_count,
  origin: getTemplateOrigin(template),
  state,
  partner: template.partner_source?.partner,
  partnerCategory: template.partner_source?.category,
  classification: template.classification,
});

export const buildTreatmentLibrary = ({
  schema,
  templates,
}: {
  schema: EncounterSchema | undefined;
  templates: DocumentTemplateSummary[];
}): WorkspaceCatalogItem[] => {
  const references = schema?.documents ?? [];
  const protocols = templates.filter((template) =>
    isProtocolTemplate(template.type),
  );
  const linked = references.flatMap((reference) => {
    const template = protocols.find(
      (item) => item.series_id === reference.template_series_id,
    );
    return template
      ? [
          toCatalogItem(
            template,
            reference.inclusion === 'always' ? 'required' : 'selectable',
          ),
        ]
      : [];
  });
  const outside = protocols
    .filter((template) =>
      references.every(
        (reference) => reference.template_series_id !== template.series_id,
      ),
    )
    .sort((first, second) => first.name.localeCompare(second.name))
    .map((template) => toCatalogItem(template, 'outside'));

  return [...linked, ...outside];
};

export const getIncludedCatalogIds = (
  items: WorkspaceCatalogItem[],
  selectedIds: string[],
) =>
  items
    .filter(
      (item) =>
        item.state === 'required' ||
        (item.state === 'selectable' && selectedIds.includes(item.id)),
    )
    .map((item) => item.id);

// Every protocol of the clinic, to open its template in the editor.
export const buildProtocolLibrary = ({
  templates,
}: {
  templates: DocumentTemplateSummary[];
}): WorkspaceCatalogItem[] =>
  templates
    .filter((template) => isProtocolTemplate(template.type))
    .sort((first, second) => first.name.localeCompare(second.name))
    .map((template) => toCatalogItem(template, 'selectable'));

// The summary and evaluation every encounter of a consultation schema
// generates, listed under the schema by their tab titles. A template several
// schemas share is listed under each of them.
export const buildConsultationLibrary = ({
  schemas,
  templates,
}: {
  schemas: EncounterSchema[];
  templates: DocumentTemplateSummary[];
}): WorkspaceCatalogItem[] => {
  const linked = schemas
    .filter((schema) => isConsultationWithoutBlockNotes(schema, templates))
    .flatMap((schema) =>
      getSchemaDocuments(schema, templates)
        .filter(({ reference, template }) => {
          const type = parseDocumentType(template.type);
          return (
            reference.inclusion === 'always' &&
            !!type &&
            CONSULTATION_DOCUMENT_TYPES.includes(type)
          );
        })
        .map(({ reference, template }) => ({
          ...toCatalogItem(template, 'selectable'),
          name: getSchemaDocumentLabel(reference, templates),
          description: template.name,
          group: schema.name,
        })),
    );
  const linkedIds = new Set(linked.map((item) => item.id));
  return [
    ...linked,
    ...templates
      .filter(
        (template) =>
          !isProtocolTemplate(template.type) &&
          ((template.type === 'SUMMARY' && template.transcriptor_v2) ||
            isClinicOwnTemplate(template)) &&
          !linkedIds.has(template.series_id),
      )
      .map((template) => toCatalogItem(template, 'selectable')),
  ];
};

// The clinic's templates from partners' systems, once each, under the
// categories the partner files them in, in alphabetical order; those of no
// category come first, without a heading. Where the clinic has templates of
// several partners, the partner is the catalog's filter.
export const buildPartnerLibrary = ({
  items,
  partnerName,
}: {
  items: WorkspaceCatalogItem[];
  partnerName: (partner: PartnerTemplatePartner) => string;
}): WorkspaceCatalogItem[] => {
  const partnerItems = items.filter(
    (item, index) =>
      item.partner !== undefined &&
      items.findIndex((other) => other.id === item.id) === index,
  );
  const several = new Set(partnerItems.map((item) => item.partner)).size > 1;
  return [...partnerItems]
    .sort(
      (first, second) =>
        (first.partnerCategory ?? '').localeCompare(
          second.partnerCategory ?? '',
        ) || first.name.localeCompare(second.name),
    )
    .map((item) => ({
      ...item,
      group: item.partnerCategory || undefined,
      category:
        several && item.partner !== undefined
          ? partnerName(item.partner)
          : undefined,
    }));
};

const VISIT_GROUP_IDS = Object.keys(templateGroupMessages);
const DEPARTMENT_IDS = Object.keys(templateDepartmentMessages);

const getVisitOrder = (asset: RegistryAsset) =>
  VISIT_GROUP_IDS.indexOf(asset.classification?.group ?? '');

// The shared community and official templates of the library, under the
// visits they are for in the vocabulary's order; those of no known visit
// come first, without a heading. A visit named as one of another department,
// as each department's consultations are, is headed with its department.
export const buildRegistryLibrary = ({
  assets,
  visitLabel,
  departmentLabel,
}: {
  assets: RegistryAsset[];
  visitLabel: (group: string) => string;
  departmentLabel: (department: string) => string;
}): WorkspaceCatalogItem[] => {
  const visits = VISIT_GROUP_IDS.filter((group) =>
    assets.some((asset) => asset.classification?.group === group),
  );
  const getHeading = (group: string) => {
    const label = visitLabel(group);
    const department = DEPARTMENT_IDS.find((id) => group.startsWith(`${id}.`));
    const named = visits.some(
      (other) => other !== group && visitLabel(other) === label,
    );
    return named && department
      ? `${departmentLabel(department)} · ${label}`
      : label;
  };

  return [...assets]
    .sort(
      (first, second) =>
        getVisitOrder(first) - getVisitOrder(second) ||
        first.name.localeCompare(second.name),
    )
    .map((asset): WorkspaceCatalogItem => {
      const visit = asset.classification?.group;
      return {
        id: asset.id,
        name: asset.name,
        description: asset.description,
        sectionsCount: asset.sectionsCount ?? 0,
        origin: asset.originKind === 'community' ? 'community' : 'official',
        state: 'selectable',
        classification: asset.classification ?? undefined,
        group: visit && visits.includes(visit) ? getHeading(visit) : undefined,
      };
    });
};
